import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";

interface GoogleTokenResponse {
	access_token?: string;
	error?: string;
}

interface GoogleTokenClient {
	requestAccessToken: () => void;
}

interface GoogleOAuth2 {
	initTokenClient: (config: {
		client_id: string;
		scope: string;
		callback: (response: GoogleTokenResponse) => void;
		error_callback?: (error: { type?: string }) => void;
	}) => GoogleTokenClient;
}

declare global {
	interface Window {
		google?: {
			accounts: {
				oauth2: GoogleOAuth2;
			};
		};
	}
}

interface Props {
	onAccessToken: (accessToken: string) => void;
	label?: string;
	className?: string;
	disabled?: boolean;
}

function GoogleLogo() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 48 48"
			width="20"
			height="20"
			aria-hidden
		>
			<path
				fill="#FFC107"
				d="M43.611 20.083H42V20H24v8h11.303C33.972 32.91 29.418 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
			/>
			<path
				fill="#FF3D00"
				d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
			/>
			<path
				fill="#4CAF50"
				d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.398 0-9.952-3.06-11.292-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
			/>
			<path
				fill="#1976D2"
				d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
			/>
		</svg>
	);
}

export default function GoogleSignInButton({
	onAccessToken,
	label = "Continue with Google",
	className,
	disabled = false,
}: Props) {
	const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID as
		| string
		| undefined;

	const tokenClientRef = useRef<GoogleTokenClient | null>(null);
	const callbackRef = useRef(onAccessToken);
	const [isReady, setIsReady] = useState(false);

	useEffect(() => {
		callbackRef.current = onAccessToken;
	}, [onAccessToken]);

	useEffect(() => {
		if (!clientId) return;

		const initClient = () => {
			if (!window.google?.accounts?.oauth2) return;
			tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
				client_id: clientId,
				scope: "openid email profile",
				callback: (response) => {
					if (response.access_token) {
						callbackRef.current(response.access_token);
					}
				},
			});
			setIsReady(true);
		};

		const scriptSrc = "https://accounts.google.com/gsi/client?hl=en";
		const existing = document.querySelector(
			'script[src*="accounts.google.com/gsi/client"]'
		);

		if (window.google?.accounts?.oauth2) {
			initClient();
		} else if (existing) {
			existing.addEventListener("load", initClient);
			return () => existing.removeEventListener("load", initClient);
		} else {
			const script = document.createElement("script");
			script.src = scriptSrc;
			script.async = true;
			script.defer = true;
			script.onload = initClient;
			document.head.appendChild(script);
		}
	}, [clientId]);

	if (!clientId) return null;

	return (
		<Button
			type="button"
			variant="white"
			disabled={disabled || !isReady}
			onClick={() => tokenClientRef.current?.requestAccessToken()}
			className={className}
		>
			<GoogleLogo />
			{label}
		</Button>
	);
}
